"use client";

import { useTranslations } from "use-intl";

import { ExploreIcon } from "@/components/appIcons";
import { DecadeSelector } from "@/components/decade-selector";
import { isDiscoveryDecade } from "@garimpa/lib/discoveryDecades";

interface SearchDiscoveryIntroProps {
  updateFilters: (updates: Record<string, string | number | undefined>) => void;
  yearFrom: number | undefined;
  yearTo: number | undefined;
}

export function SearchDiscoveryIntro({
  updateFilters,
  yearFrom,
  yearTo,
}: SearchDiscoveryIntroProps) {
  const t = useTranslations("globals.search");
  const selectedDecade =
    yearFrom != null && yearTo === yearFrom + 9 && isDiscoveryDecade(yearFrom)
      ? yearFrom
      : undefined;

  return (
    <div className="mb-6 space-y-5">
      <div className="grid items-center gap-4 sm:grid-cols-[minmax(0,1fr)_auto]">
        <div>
          <div className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-primary">
            <ExploreIcon className="text-lg" />
            {t("catalog")}
          </div>
          <h2 className="max-w-2xl text-3xl font-black tracking-tight sm:text-4xl">
            {t("discoverTitle")}
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
            {t("discoverSubtitle")}
          </p>
        </div>
        <div
          aria-hidden
          className="relative hidden size-24 shrink-0 place-items-center rounded-full border-[9px] border-neutral-950 bg-neutral-950 shadow-md sm:grid lg:size-28"
        >
          <span className="absolute inset-2 rounded-full border border-background/20" />
          <span className="absolute inset-5 rounded-full border border-background/20" />
          <span className="grid size-10 place-items-center rounded-full bg-primary text-primary-foreground lg:size-12">
            <span className="size-2 rounded-full bg-background" />
          </span>
        </div>
      </div>
      <div className="rounded-2xl border border-border/80 bg-background/70 p-4">
        <h3 className="mb-3 text-xs font-bold uppercase tracking-widest text-muted-foreground sm:mb-4">
          {t("discoverEras")}
        </h3>
        <DecadeSelector
          value={selectedDecade}
          onChange={(decade) => {
            updateFilters({
              yearFrom: decade,
              yearTo: decade == null ? undefined : decade + 9,
            });
          }}
        />
      </div>
    </div>
  );
}
